import {useState} from 'react';

import {useAssignVehicleWarrantyMutation} from '@dms/api/sales';
import {handleApiError} from '@dms/shared';

import {VehicleWarrantyRow} from '../../core/schema/VehicleWarrantyRowSchema';

export interface SyncWarrantiesParams {
  vehicleId: string;
  warranties: VehicleWarrantyRow[];
}

/**
 * Posts the warranties collected on the create form once the vehicle exists.
 * The vehicle creation stays successful even when a warranty assignment fails - the failure is
 * only reported through a notification, like the document sync.
 *
 * @example
 * const {syncWarranties} = useVehicleWarrantySync();
 *
 * const handleSubmit = async (values) => {
 *   const vehicle = await createVehicle(values).unwrap();
 *
 *   await syncWarranties({vehicleId: vehicle.id, warranties: values.warranties ?? []});
 * };
 */
export function useVehicleWarrantySync() {
  const [assignVehicleWarranty] = useAssignVehicleWarrantyMutation();
  const [isSyncing, setIsSyncing] = useState(false);

  const syncWarranties = async (params: SyncWarrantiesParams) => {
    setIsSyncing(true);

    try {
      // The backend serializes warranty assignments, so parallel requests are safe.
      await Promise.all(
        params.warranties.map((warranty) =>
          assignVehicleWarranty({
            vehicleId: params.vehicleId,
            assignWarrantyProductRequestBody: {
              ...warranty,
              // Read-only rows are reserved for OEM/ESB sourced assignments.
              isReadOnly: false,
            },
          })
            .unwrap()
            .catch(handleApiError)
        )
      );
    } finally {
      setIsSyncing(false);
    }
  };

  return {syncWarranties, isSyncing};
}
